import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import {
  useEffect,
  useState,
  type ComponentProps,
  type ReactNode,
} from 'react';
import { FormattedMessage } from 'react-intl';
import { usePrevious } from 'react-use';
import styled, { keyframes } from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import type { Color } from '@sorare/core/src/atoms/buttons/Button';
import { LoadingButton } from '@sorare/core/src/atoms/buttons/LoadingButton';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { GemIcon } from '@sorare/core/src/atoms/icons/GemIcon';
import { Shards } from '@sorare/core/src/atoms/icons/Shards';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { BrandS } from '@sorare/core/src/atoms/typography';
import {
  DialogAdditionalParamKey,
  DialogKey,
} from '@sorare/core/src/components/navigation/WithDialogs';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { useOpenDialog } from '@sorare/core/src/hooks/navigation/useOpenDialog';
import { ensureHexColor } from '@sorare/core/src/lib/colors';
import { getCommonCardShardsCount } from '@sorare/core/src/lib/currentUser';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';
import { glossary } from '@sorare/core/src/lib/glossary';
import {
  ClickBuyPack_Currency,
  ClickBuyPack_Target,
  OpenBuyGemsDialog_Context,
} from '@sorare/core/src/protos/events/so5/shared/events';
import {
  useGetRarityShardColor,
  useIsSpecialCardSet,
} from '@sorare/core/src/set';

import { RevealPackCardsDialog } from '#src/components/commonCard/RevealPackCardsDialog/index.tsx';
import { useBuyCardPack } from '#src/hooks/packs/useBuyCardPack.ts';

import type { BuyPackButton_cardPack } from './__generated__/index.graphql.ts';

const decrementAnimation = keyframes`
  0% {
    opacity: 0;
  }
  20% {
    opacity: 1;
  }
  25% {
    bottom: calc(100% + var(--double-unit));
    transform: translate(-50%, 50%) scale(1.1);
  }
  80% {
    opacity: 1;
  }

  99% {
    opacity: 0;
  }
  100% {
    bottom: calc(100% + var(--double-unit));
    display: none;
  }
`;

const Relative = styled.div`
  position: relative;
  margin: 0 auto;
`;

const Decrement = styled.div`
  bottom: 0;
  opacity: 0;
  position: absolute;
  left: 50%;
  transform: translate(-50%, 50%);

  &.loading {
    animation: ${decrementAnimation} 1s cubic-bezier(0.02, 0.36, 0.34, 0.97)
      forwards;
  }
`;
const NoWrapBrandS = styled(BrandS)`
  white-space: nowrap;
`;

type Props = Omit<
  Partial<ComponentProps<typeof LoadingButton>>,
  'children' | 'loading'
> & {
  cardPack: Nullable<BuyPackButton_cardPack>;
  children?: (price: ReactNode) => ReactNode;
  confirmPurchaseColor?: Color;
  onPurchaseSuccess?: () => void;
};

export const BuyPackButton = ({
  cardPack,
  children,
  color,
  confirmPurchaseColor,
  onPurchaseSuccess,
  ...otherProps
}: Props) => {
  const isSpecialCardSet = useIsSpecialCardSet();
  const getRarityShardColor = useGetRarityShardColor();
  const track = useEvents();
  const { currentUser } = useCurrentUserContext();
  const { sport } = useSportContext();
  const openDialog = useOpenDialog();
  const isGemsPack = cardPack?.currency === 'COMMON_GEM';
  const isLaptop = useIsLaptop();
  const [confirmPurchase, setConfirmPurchase] = useState(false);
  const currentCardPackSlug = cardPack?.slug || '';
  const prevCardPackSlug = usePrevious(currentCardPackSlug);
  const {
    loading,
    onPurchase,
    packNotBought,
    useNativeIOSPackOpening,
    onMobilePurchase,
    buyCardPackData,
    onCloseRevealDialog,
  } = useBuyCardPack(cardPack?.slug);

  const userBalance = isGemsPack
    ? currentUser?.gemsBalances?.[0]?.amount || 0
    : getCommonCardShardsCount(currentUser, cardPack?.cardSet?.slug) || 0;

  const gemsNeeded = (cardPack?.price || 0) - userBalance;
  const insufficientBalance = gemsNeeded > 0;

  const shardColor =
    (cardPack?.cardSet?.slug &&
      isSpecialCardSet(cardPack.cardSet.slug) &&
      getRarityShardColor(Rarity.common, cardPack.cardSet.slug)) ||
    'var(--c-black)';

  const handlePurchase = () => {
    if (useNativeIOSPackOpening) {
      onMobilePurchase();
    } else {
      onPurchase();
    }
  };

  const trackClickBuyPack = (target: ClickBuyPack_Target) => {
    const currency =
      cardPack?.currency === 'COMMON_GEM'
        ? ClickBuyPack_Currency.GEM
        : ClickBuyPack_Currency.ESSENCE;
    track('Click Buy Pack', {
      packSlug: cardPack?.slug || '',
      currency,
      amount: cardPack?.price || 0,
      cardSetSlug: cardPack?.cardSet?.slug || '',
      target,
      sport,
    });
  };
  const trackOpenBuyGemsDialog = () => {
    track('Open Buy Gems Dialog', {
      context: OpenBuyGemsDialog_Context.BUY_PACK_BUTTON,
      packSlug: cardPack?.slug || '',
      sport,
    });
  };

  useEffect(() => {
    if (
      confirmPurchase &&
      currentCardPackSlug &&
      currentCardPackSlug !== prevCardPackSlug
    ) {
      setConfirmPurchase(false);
    }
  }, [
    confirmPurchase,
    setConfirmPurchase,
    currentCardPackSlug,
    prevCardPackSlug,
  ]);

  useEffect(() => {
    if (confirmPurchase && !isLaptop) {
      setTimeout(() => {
        setConfirmPurchase(false);
      }, 6000);
    }
  }, [confirmPurchase, isLaptop]);

  const currencyIcon = isGemsPack ? (
    <GemIcon size={2.5} />
  ) : (
    !!shardColor && <Shards size={2} color={shardColor} />
  );
  const price = (
    <>
      {cardPack?.price} {currencyIcon}
    </>
  );
  return (
    <>
      <Relative>
        <Decrement className={classNames({ loading })}>
          <NoWrapBrandS color="var(--c-red-400)">- {price}</NoWrapBrandS>
        </Decrement>
        <LoadingButton
          disabled={!!insufficientBalance && !isGemsPack}
          size="large"
          loading={loading}
          color={
            confirmPurchase
              ? confirmPurchaseColor || 'secondary'
              : color || 'primary'
          }
          {...otherProps}
          onClick={() => {
            if (!!insufficientBalance && !!isGemsPack && !loading) {
              trackClickBuyPack(ClickBuyPack_Target.BUY_GEMS_DIALOG);
              trackOpenBuyGemsDialog();
              openDialog(DialogKey.buyGems, 'true', {
                additionalParams: {
                  [DialogAdditionalParamKey.gemsNeeded]: gemsNeeded.toString(),
                },
              });
              return;
            }
            trackClickBuyPack(ClickBuyPack_Target.CONFIRM);
            if (!confirmPurchase) {
              setConfirmPurchase(true);
              return;
            }
            setConfirmPurchase(false);
            handlePurchase();
            onPurchaseSuccess?.();
          }}
        >
          <Horizontal center gap={0.5} as="span">
            {confirmPurchase ? (
              <>
                <FontAwesomeIcon icon={faCheck} />
                <FormattedMessage {...glossary.confirm} />
              </>
            ) : (
              children?.(price) || price
            )}
          </Horizontal>
        </LoadingButton>
      </Relative>
      {!!cardPack && !!buyCardPackData?.cards && (
        <RevealPackCardsDialog
          open={!packNotBought}
          onClose={onCloseRevealDialog}
          cards={buyCardPackData.cards}
          onContinue={onCloseRevealDialog}
          cardSetSlug={cardPack.cardSet?.slug || ''}
          packId={buyCardPackData?.pack?.id || ''}
          packPictureUrl={cardPack?.pictureUrl || ''}
          primaryColor={ensureHexColor(cardPack?.primaryColor || '')}
          secondaryColor={ensureHexColor(cardPack?.secondaryColor || '')}
          showPlayerDetails
        />
      )}
    </>
  );
};
BuyPackButton.fragments = {
  cardPack: gql`
    fragment BuyPackButton_cardPack on CardPack {
      slug
      currency
      price
      pictureUrl
      primaryColor: color(variant: PRIMARY)
      secondaryColor: color(variant: SECONDARY)
      cardSet {
        slug
      }
    }
  ` as TypedDocumentNode<BuyPackButton_cardPack>,
};
